import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { apiEndpoints } from '../../../api/endpoints';
import { workflowApi } from '../../../api/workflow';
import { testDataApi } from '../../../api/testdata';
import { apicallApi } from '../../../api/apicall';
import Navbar from '../../layout/Navbar';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { startBatchStepsPolling, handleResetScreenshot } from '../../../utils/browserSessionHelpers';
import ScreenshotPreview from '../../sections/ScreenshotPreview';
import { Search, X, Check } from 'lucide-react';
import DataFixture from '../../sections/DataFixture';
import TestStepForm from '../../sections/TestStepForm';
import SavedSteps from '../../sections/SavedSteps';
import GeneratedSteps from '../../sections/GeneratedSteps';
import { v4 as uuidv4 } from 'uuid';

const TEST_ACTIONS = [
  { 
    value: 'type', 
    label: 'Type Text',
    fields: [
      { name: 'value', label: 'Text to type', placeholder: 'Enter text to type' },
      { name: 'element', label: 'Element to type into', placeholder: 'Type name of input here' }
    ]
  },
  { 
    value: 'click', 
    label: 'Click Element',
    fields: [
      { name: 'element', label: 'Element to click', placeholder: 'Type element to click' }
    ]
  },
  { 
    value: 'navigate', 
    label: 'Navigate to URL',
    fields: [
      { name: 'value', label: 'URL', placeholder: 'Enter URL to navigate to' }
    ]
  },
  { 
    value: 'select', 
    label: 'Select Option',
    fields: [
      { name: 'value', label: 'Option to Select', placeholder: 'Enter option text or value' },
      { name: 'element', label: 'Dropdown Element', placeholder: 'Type name of dropdown' }
    ]
  },
  { 
    value: 'enter', 
    label: 'Press Enter',
    fields: []
  },
  { 
    value: 'workflow', 
    label: 'Use Workflow',
    fields: [
      { name: 'workflowId', label: 'Select Workflow', placeholder: 'Choose a workflow' },
      { name: 'repeatCount', label: 'Repeat Count', placeholder: 'Number of times to repeat' }
    ]
  },
  {
    value: 'assert',
    label: 'Add Assertion',
    fields: [
      { name: 'value', label: 'Assertion', placeholder: 'Enter assertion text' }
    ]
  },
  {
    value: 'wait',
    label: 'Wait',
    fields: [
      {
        name: 'value',
        label: 'Wait time (seconds)',
        placeholder: 'Enter wait time in seconds',
        type: 'number'
      }
    ]
  },
  {
    value: 'reload',
    label: 'Reload Page',
    fields: []
  }
];

function CreateTestCase() {
  const [sessionTestCases, setSessionTestCases] = useState([]);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    testname: '',
    currentStep: { action: 'type', value: '', element: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 },
    savedSteps: [],
    assertion: '',
    editingStepIndex: undefined
  });
  
  // Add state for API calls
  const [apiCallMode, setApiCallMode] = useState(false);
  const [apiCall, setApiCall] = useState({
    name: '',
    method: 'GET',
    url: '',
    body: '',
    position: 'before' // 'before' or 'after' test steps
  });
  const [savedApiCalls, setSavedApiCalls] = useState([]);
  const [editingApiCallIndex, setEditingApiCallIndex] = useState(undefined);
  
  // Add state for existing API calls
  const [availableApiCalls, setAvailableApiCalls] = useState([]);
  const [selectedApiCallId, setSelectedApiCallId] = useState('');
  const [isLoadingApiCalls, setIsLoadingApiCalls] = useState(false);
  const [apiCallsError, setApiCallsError] = useState(null);

  // Add state for step screenshots
  const [stepScreenshot, setStepScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  
  const { suiteId } = useParams();
  const navigate = useNavigate();

  const [sessionId, setSessionId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [initialScreenshot, setInitialScreenshot] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [isAssertionGenerating, setIsAssertionGenerating] = useState(false);
  const [genratedAssertions, setGeneratedAssertions] = useState([]);
  const [isGeneratingSteps, setIsGeneratingSteps] = useState(false);
  const [generatedSteps, setGeneratedSteps] = useState([]);
  const [pendingSteps, setPendingSteps] = useState([]);
  const [showGeneratedSteps, setShowGeneratedSteps] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  
  // Fixture selection state
  const [isFixtureModalOpen, setIsFixtureModalOpen] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);

  // Add useEffect for initialization
  useEffect(() => {
    const initializeBrowserSession = async () => {
      try {
        const result = await apiEndpoints.startBrowserSession(suiteId);
        
        // Set the session ID
        setSessionId(result.sessionId);
        
        // Set initial screenshot if available
        if (result.screenshot?.data) {
          setInitialScreenshot(`data:image/jpeg;base64,${result.screenshot.data}`);
        }

      } catch (error) {
        console.error('Failed to initialize browser session:', error);
        setError('Failed to start browser session: ' + error.message);
      }
    };

    if (suiteId) {
      initializeBrowserSession();
    }
  }, [suiteId]); // Only run when suiteId changes

  // Add useEffect to fetch workflows for the test suite
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        if (suiteId) {
          const workflows = await workflowApi.getWorkflowsBySuite(suiteId);
          setAvailableWorkflows(workflows || []);
        }
      } catch (error) {
        console.error('Failed to fetch workflows:', error);
        setError('Failed to load available workflows');
      }
    };

    fetchWorkflows();
  }, [suiteId]);

  // Add useEffect to fetch available API calls
  useEffect(() => {
    const fetchAvailableApiCalls = async () => {
      try {
        setIsLoadingApiCalls(true);
        setApiCallsError(null);
        
        // Use the apicallApi service to fetch all available API calls
        const apiCalls = await apicallApi.getAllApiCalls();
        setAvailableApiCalls(apiCalls);
        
        setIsLoadingApiCalls(false);
      } catch (error) {
        console.error('Failed to fetch API calls:', error);
        setApiCallsError('Failed to load available API calls');
        setIsLoadingApiCalls(false);
      }
    };

    fetchAvailableApiCalls();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSaveStep = async () => {
    const { action, value, element, exact, workflowId, secret, value_mode, repeatCount } = formData.currentStep;
    
    // For click actions, respect the exact flag but don't modify the text
    const exactMatch = action === 'click' ? exact : false;
    
    // Helper function to extract text within brackets if needed
    const getSelector = (text, isExact) => {
      if (!text) return '';
      
      // If exact match is enabled, extract text within {{ }} brackets
      if (isExact) {
        const bracketMatch = text.match(/\{\{([^}]+)\}\}/);
        if (bracketMatch && bracketMatch[1]) {
          return bracketMatch[1].trim();
        }
      }
      
      // Otherwise return the full text
      return text;
    };
    
    // Check if we're editing an existing step
    if (formData.editingStepIndex !== undefined) {
      // Validate based on action type
      if (action === 'workflow') {
        if (!workflowId) {
          setError('Please select a workflow');
          return;
        }
        
        // Find the selected workflow to get its name
        const selectedWorkflow = availableWorkflows.find(w => w.id === workflowId);
        if (!selectedWorkflow) {
          setError('Selected workflow not found');
          return;
        }
        
        // Validate repeat count
        const repeatCountValue = parseInt(repeatCount) || 1;
        if (repeatCountValue < 1) {
          setError('Repeat count must be at least 1');
          return;
        }
        
        // Update the existing step
        setFormData(prevState => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = { 
            action: 'workflow',
            workflowId,
            workflowName: selectedWorkflow.name,
            repeatCount: repeatCountValue,
            order: updatedSteps[prevState.editingStepIndex].order,
            exact: false
          };
          
          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 },
            editingStepIndex: undefined // Clear editing state
          };
        });
        setError(null);
        return;
      }
      
      // Validate other step types
      if (action === 'navigate' && !value) {
        setError('Please enter a URL');
        return;
      }
      
      if (action === 'assert' && !value) {
        setError('Please enter an assertion');
        return;
      }
      
      if (action === 'wait') {
        if (!value || parseFloat(value) <= 0) {
          setError('Wait time must be a positive number');
          return;
        }
        
        // Convert seconds to milliseconds for backend
        const waitTimeMs = Math.round(parseFloat(value) * 1000).toString();
        
        // Update the existing step
        setFormData(prevState => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = { 
            action,
            value: waitTimeMs, // Store as milliseconds string
            element: '',
            exact: false,
            secret: false,
            value_mode: 'static',
            order: updatedSteps[prevState.editingStepIndex].order,
            metadata: {
              actionType: action,
              selector: '',
              value: waitTimeMs
            }
          };
          
          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 },
            editingStepIndex: undefined
          };
        });
        setError(null);
        return;
      }
      
      if (action !== 'enter' && action !== 'navigate' && action !== 'assert' && !element) {
        setError('Please enter an element');
        return;
      }
      
      // Update the existing step
      setFormData(prevState => {
        const updatedSteps = [...prevState.savedSteps];
        
        // Handle screenshot URL if present
        let screenshotUrl = null;
        if (stepScreenshot) {
          // Check if stepScreenshot is a URL string or file object
          screenshotUrl = typeof stepScreenshot === 'string' ? stepScreenshot : stepScreenshot?.uploadedUrl;
        }
        
        updatedSteps[prevState.editingStepIndex] = { 
          action,
          value: value || '',
          element: element || '',  // Pass the element text as-is
          exact: exactMatch,
          secret: action === 'type' ? secret : false, // Only include secret for type actions
          value_mode: action === 'type' || action === 'navigate' ? value_mode : 'static', // Include value_mode for type and navigate actions
          order: updatedSteps[prevState.editingStepIndex].order,
          ...(action === 'assert' && { assertion_mode: formData.currentStep.assertion_mode || 'flexible' }),
          metadata: {
            actionType: action,
            selector: action === 'click' ? getSelector(element, exactMatch) : (element || ''),
            value: value || '',
            ...(action === 'assert' && { assertion: value || '', assertion_mode: formData.currentStep.assertion_mode || 'flexible' }),
            ...(action === 'click' && { exact: exactMatch }),
            ...(action === 'type' && { 
              secret: secret,
              value_mode: value_mode
            }),
            ...(action === 'navigate' && {
              value_mode: value_mode
            }),
            ...(screenshotUrl && { step_screenshot: screenshotUrl })
          }
        };
        
        return {
          ...prevState,
          savedSteps: updatedSteps,
          currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 },
          editingStepIndex: undefined // Clear editing state
        };
      });
      
      // Clear screenshot after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      setError(null);
      return;
    }
    
    // Original code for adding a new step
    // Calculate the next order number
    const nextOrder = formData.savedSteps.length + 1;
    
    // Validate based on action type
    if (action === 'workflow') {
      if (!workflowId) {
        setError('Please select a workflow');
        return;
      }
      
      // Find the selected workflow to get its name
      const selectedWorkflow = availableWorkflows.find(w => w.id === workflowId);
      if (!selectedWorkflow) {
        setError('Selected workflow not found');
        return;
      }
      
      // Validate repeat count
      const repeatCountValue = parseInt(repeatCount) || 1;
      if (repeatCountValue < 1) {
        setError('Repeat count must be at least 1');
        return;
      }
      
      // Save workflow step
      setFormData(prevState => ({
        ...prevState,
        savedSteps: [...prevState.savedSteps, { 
          id: uuidv4(),
          action: 'workflow',
          workflowId: workflowId,
          workflowName: selectedWorkflow.name,
          repeatCount: repeatCountValue,
          order: nextOrder,
          exact: false,
        }],
        currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 }
      }));
      setError(null);
      return;
    }
    
    // Validate other step types
    if (action === 'navigate' && !value) {
      setError('Please enter a URL');
      return;
    }
     if (action === 'assert' && !value) {
      setError('Please enter an assertion');
      return;
    }
    
    if (action === 'wait') {
      if (!value || parseFloat(value) <= 0) {
        setError('Wait time must be a positive number');
        return;
      }
      
      // Convert seconds to milliseconds for backend
      const waitTimeMs = Math.round(parseFloat(value) * 1000).toString();
      
      // Save wait step
      setFormData(prevState => ({
        ...prevState,
        savedSteps: [...prevState.savedSteps, { 
          id: uuidv4(),
          action,
          value: waitTimeMs, // Store as milliseconds string
          element: '',
          exact: false,
          secret: false,
          value_mode: 'static',
          order: nextOrder,
          metadata: {
            actionType: action,
            selector: '',
            value: waitTimeMs
          }
        }],
        currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 }
      }));
      setError(null);
      return;
    }
    
    if (action === 'reload') {
      setFormData(prevState => ({
        ...prevState,
        savedSteps: [...prevState.savedSteps, { 
          id: uuidv4(),
          action,
          value: '',
          element: '',
          exact: false,
          secret: false,
          value_mode: 'static',
          order: nextOrder,
          metadata: {
            actionType: action,
            selector: '',
            value: ''
          }
        }],
        currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 }
      }));
      setError(null);
      return;
    }

    if (action !== 'enter' && action !== 'navigate' && action !== 'assert' && action !== 'wait' && action !== 'reload' && !element) {
      setError('Please enter an element');
      return;
    }
    
    // Handle screenshot URL if present
    let screenshotUrl = null;
    console.log('Step screenshot:', stepScreenshot);
    if (stepScreenshot) {
      // The screenshot is already uploaded and stepScreenshot contains the URL
      screenshotUrl = stepScreenshot?.uploadedUrl
    }

    // Save non-workflow step
    setFormData(prevState => ({
      ...prevState,
      savedSteps: [...prevState.savedSteps, { 
        id: uuidv4(),
        action,
        value: value || '',
        element: element || '',  // Pass the element text as-is
        exact: exactMatch,
        secret: action === 'type' ? secret : false, // Only include secret for type actions
        value_mode: action === 'type' || action === 'navigate' ? value_mode : 'static', // Include value_mode for type and navigate actions
        order: nextOrder,
        ...(action === 'assert' && { assertion_mode: formData.currentStep.assertion_mode || 'flexible' }),
        metadata: {
          actionType: action,
          selector: action === 'click' ? getSelector(element, exactMatch) : (element || ''),
          value: value || '',
          ...(action === 'assert' && { assertion: value || '', assertion_mode: formData.currentStep.assertion_mode || 'flexible' }),
          ...(action === 'click' && { exact: exactMatch }),
          ...(action === 'type' && { 
            secret: secret,
            value_mode: value_mode
          }),
          ...(action === 'navigate' && {
            value_mode: value_mode
          }),
          ...(screenshotUrl && { step_screenshot: screenshotUrl })
        }
      }],
      currentStep: { action: 'type', value: '', element: '', workflowId: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 }
    }));

    // Clear screenshot after saving
    setStepScreenshot(null);
    setScreenshotPreview(null);
    setError(null);
  };

  // Handler for saving API calls
  const handleSaveApiCall = () => {
    // Validate the API call selection
    if (!selectedApiCallId) {
      setError('Please select an API call');
      return;
    }
    
    // Find the selected API call from available API calls
    const selectedApiCall = availableApiCalls.find(call => call.id === selectedApiCallId);
    if (!selectedApiCall) {
      setError('Selected API call not found');
      return;
    }
    
    // Check if we're editing an existing API call
    if (editingApiCallIndex !== undefined) {
      setSavedApiCalls(prev => {
        const updated = [...prev];
        updated[editingApiCallIndex] = { 
          id: selectedApiCall.id,
          name: selectedApiCall.name,
          method: selectedApiCall.method,
          url: selectedApiCall.url,
          order: updated[editingApiCallIndex].order
        };
        return updated;
      });
      
      // Reset the form
      setSelectedApiCallId('');
      setApiCall({
        name: '',
        method: 'GET',
        url: '',
        body: ''
      });
      setEditingApiCallIndex(undefined);
      setError(null);
      return;
    }
    
    // Add a new API call reference with the next order number
    const nextOrder = savedApiCalls.length + formData.savedSteps.length + 1;
    setSavedApiCalls(prev => [...prev, { 
      id: selectedApiCall.id,
      name: selectedApiCall.name,
      method: selectedApiCall.method,
      url: selectedApiCall.url,
      order: nextOrder
    }]);
    
    // Reset the form
    setSelectedApiCallId('');
    setApiCall({
      name: '',
      method: 'GET',
      url: '',
      body: ''
    });
    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveStep();
    }
  };

  const handleRemoveStep = (index) => {
    setFormData(prev => ({
      ...prev,
      savedSteps: prev.savedSteps.filter((_, i) => i !== index)
    }));
  };

  const handleEditApiCall = (index) => {
    // Get the API call to edit
    const apiCallToEdit = savedApiCalls[index];
    
    // Set the form values
    setApiCall({ 
      ...apiCallToEdit,
      // Only keep the position from the saved API call
      name: '',
      method: 'GET',
      url: '',
      body: ''
    });
    
    // Set the selected API call ID
    setSelectedApiCallId(apiCallToEdit.id);
    
    // Set editing mode
    setEditingApiCallIndex(index);
    
    // Switch to API call mode if not already
    setApiCallMode(true);
  };
  
  const handleRemoveApiCall = (index) => {
    setSavedApiCalls(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerateAssertion = async (e) => {
    e.preventDefault();
    setIsAssertionGenerating(true);

    const formattedSteps = formData.savedSteps.map(step => {
      // Base step structure
      const formattedStep = {
        order: step.order,
        existingStepId: step.id || null,
        newStepData: {
          name: formatStepName(step),
          metadata: {
            actionType: step.action,
            selector: step.element,
            value: step.value,
            ...(step.action === 'click' && { exact: step.exact }),
            ...(step.action === 'type' && { secret: step.secret }),
            ...(step.action === 'navigate' && { value_mode: step.value_mode }),
            ...(step.action === 'assert' && { assertion: step.value, assertion_mode: step.assertion_mode || 'flexible' }),
          }
        },
        subWorkflowId: null
      };
      
      // For workflow steps, add the workflow ID to both metadata and subWorkflowId
      if (step.action === 'workflow' && step.workflowId) {
        formattedStep.newStepData.metadata.workflowId = step.workflowId;
        formattedStep.newStepData.metadata.repeatCount = step.repeatCount || 1;
        formattedStep.subWorkflowId = step.workflowId;
      }
      
      return formattedStep;
    });

    const screenshotsToUse = screenshots.length > 3 ? screenshots.slice(-3) : screenshots;

    const data = await apiEndpoints.generateAssertion(formData.testname,formattedSteps,screenshotsToUse);


    if(data.status === "success") {
    setGeneratedAssertions(data.assertions);
    }
    setIsAssertionGenerating(false);
  }

  const handleAcceptAssertion = (assertion) => {
    setFormData((prev) => ({
      ...prev,
      assertion: assertion,
    }));
    
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.testname.trim()) {
      alert('Please provide a test name');
      return;
    }

    if (formData.savedSteps.length === 0 && savedApiCalls.length === 0) {
      alert('Please add at least one test step or API call');
      return;
    }

    try {
      const extractReferencedStepIds = (text) => {
        if (!text) return [];
        const matches = Array.from(text.matchAll(/\{\{\s*step:([a-f0-9\-]+)\.value\s*\}\}/gi));
        const ids = matches.map(m => m[1]);
        return Array.from(new Set(ids));
      };
      // Helper function to extract text within brackets if needed
      const getSelector = (text, isExact) => {
        if (!text) return '';
        
        // If exact match is enabled, extract text within {{ }} brackets
        if (isExact) {
          const bracketMatch = text.match(/\{\{([^}]+)\}\}/);
          if (bracketMatch && bracketMatch[1]) {
            return bracketMatch[1].trim();
          }
        }
        
        // Otherwise return the full text
        return text;
      };
      
      // Format normal steps
      const formattedRegularSteps = formData.savedSteps.map(step => {
        // Process the selector based on action type and exact flag
        const selector = step.action === 'click' 
          ? getSelector(step.element, step.exact) 
          : (step.element || '');
          
          console.log('Formatted selector:', step);
        // Base step structure
        const formattedStep = {
          order: step.order,
          existingStepId: step.id || null,
          newStepData: {
            name: formatStepName(step),
            metadata: {
              actionType: step.action,
              selector: selector,
              value: step.value,
              ...(step.action === 'click' && { exact: step.exact }),
              ...(step.action === 'type' && { secret: step.secret }),
              ...(step.action === 'navigate' && { value_mode: step.value_mode }),
              ...(step.action === 'assert' && (() => {
                const refIds = extractReferencedStepIds(step.value);
                const meta = { assertion: step.value, assertion_mode: step.assertion_mode || 'flexible' };
                if (refIds.length > 0) {
                  meta.referenced_step_id = refIds[0];
                }
                return meta;
              })()),
              ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
            }
          },
          subWorkflowId: null
        };
        
        // For workflow steps, add the workflow ID to both metadata and subWorkflowId
        if (step.action === 'workflow' && step.workflowId) {
          formattedStep.newStepData.metadata.workflowId = step.workflowId;
          formattedStep.newStepData.metadata.repeatCount = step.repeatCount || 1;
          formattedStep.subWorkflowId = step.workflowId;
        }
        
        return formattedStep;
      });

      // Format API call steps
      const formattedApiCallSteps = savedApiCalls.map(call => ({
        order: call.order || 0,
        existingStepId: null,
        newStepData: {
          name: `API Call: ${call.name}`,
          metadata: {
            actionType: 'apicall',
            endpoint_id: call.id
          }
        },
        subWorkflowId: null
      }));

      // Combine all steps and ensure sequential ordering
      const finalSteps = [...formattedRegularSteps, ...formattedApiCallSteps]
        .sort((a, b) => a.order - b.order)
        .map((step, index) => ({
          ...step,
          order: index + 1
        }));

      const testCaseData = {
        testname: formData.testname,
        assertion: formData.assertion || "No description provided",
        steps: finalSteps
      };

      const response = await apiEndpoints.createTestCase(suiteId, testCaseData);
      setSessionTestCases(prevCases => [...prevCases, response]);

      // Reset form
      setFormData({
        testname: '',
        currentStep: { action: 'type', value: '', element: '', exact: false, secret: false, value_mode: 'static', repeatCount: 1 },
        savedSteps: [],
        assertion: '',
        editingStepIndex: undefined
      });
      setSavedApiCalls([]);

      // Redirect to test cases page with a success message
      navigate(`/testsuites/${suiteId}/tests`);
    } catch (error) {
      console.error('Error creating test case:', error);
      setError('Failed to create test case');
    }
  };

  const handleGenerateSteps = async () => {
    try {
      setIsGeneratingSteps(true);
      setError(null);

      // Get the latest screenshot data
      const currentScreenshot = screenshots.length > 0 
        ? screenshots[screenshots.length - 1] 
        : initialScreenshot;

      // Remove the data:image/jpeg;base60, prefix if it exists
      const screenshotData = currentScreenshot?.replace(/^data:image\/jpeg;base64,/, '');

      const response = await apiEndpoints.generateSteps(formData.testname, screenshotData);
      
      if (response.status === "success" && response.steps) {
        // Convert API response format to our pending steps format
        const formattedSteps = response.steps.map(step => ({
          action: step.actionType,
          value: step.value || '',
          element: step.selector || '',
          exact: step.exact || false,
          status: 'pending'
        }));
        
        setPendingSteps(formattedSteps);
        setShowGeneratedSteps(true);
      }
    } catch (error) {
      console.error('Error generating steps:', error);
      setError('Failed to generate steps: ' + error.message);
    } finally {
      setIsGeneratingSteps(false);
    }
  };

  const handleAcceptSteps = () => {
    // Get the current number of existing steps
    const currentStepsCount = formData.savedSteps.length;
    
    // Convert pending steps to saved steps and append to existing steps
    const formattedSteps = pendingSteps.map((step, index) => ({
      action: step.action,
      value: step.value || '',
      element: step.element || '',
      exact: false,
      secret: step.action === 'type' && step.element.toLowerCase().includes('password'),
      order: currentStepsCount + index + 1
    }));
    
    setFormData(prev => ({
      ...prev,
      savedSteps: [...prev.savedSteps, ...formattedSteps]
    }));
    
    // Clear pending steps and hide generated steps section
    setPendingSteps([]);
    setShowGeneratedSteps(false);
  };

  const handleRejectSteps = () => {
    // Clear pending steps and hide generated steps section
    setPendingSteps([]);
    setShowGeneratedSteps(false);
  };

  const handleRunAllSteps = async (fixtureId = null) => {
    const combinedSteps = getCombinedStepsForDisplay();
    if (combinedSteps.length === 0) {
      alert('Please add at least one test step');
      return;
    }
    if (isPolling) {
        try {
          await apiEndpoints.cancelBrowserSession(sessionId);
          
          // Clear polling interval if it exists
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          
          setIsPolling(false);
          
          // Set final status
          setTestStatus({
            status: "completed",
            description: "Test execution cancelled by user"
          });
          
        } catch (error) {
          console.error("Error cancelling test execution:", error);
          setError(`Failed to cancel test execution: ${error.message}`);
        }
        return;
    }

    try {
      if (!sessionId) {
        setError('No active browser session');
        return;
      }

      setIsPolling(true);
      setTestStatus(null);

      // Format the steps before sending them
      const formattedSteps = combinedSteps.map(step => {
        if (step.isApiCall) {
          return {
            action: 'api-request',
            actionType: 'api-request',
            endpoint_id: step.id
          };
        }

        // For click actions with exact match, extract the text from within brackets
        let elementValue = step.element || '';
        
        if (step.action === 'click' && step.exact) {
          const bracketMatch = elementValue.match(/\{\{([^}]+)\}\}/);
          if (bracketMatch && bracketMatch[1]) {
            elementValue = bracketMatch[1].trim();
          }
        }
        
        // Check if the navigate value is in curly braces, indicating a fixture
        const isNavigateFixture = step.action === 'navigate' && 
          step.value && 
          /^\{[^{}]+\}$/.test(step.value.trim());

        // Set value_mode for navigate steps - if step already has a value_mode, use that instead of auto-detecting
        const navigateValueMode = step.value_mode || (isNavigateFixture ? 'fixture' : 'static');
console.log("step2>>>", step);
        // Each step should be formatted for API execution
        return {
          action: step.action,
          ...(step.action === 'workflow' 
            ? { 
                workflowId: step.workflowId,
                repeatCount: step.repeatCount || 1
              }
            : {
                element: elementValue || undefined,
                value: step.value || undefined,
                exact: step.exact || false,
                ...(step.action === 'type' && { secret: step.secret }),
                ...(step.action === 'type' && { value_mode: step.value_mode }),
                ...(step.action === 'navigate' && { value_mode: navigateValueMode }),
                ...(step.action === 'assert' && { assertion: step.value, assertion_mode: step.assertion_mode || 'flexible' }),
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
              }
          )
        };
      });

      // Pass the formatted steps with optional fixture ID
      await apiEndpoints.executeBrowserSteps(formattedSteps, suiteId, sessionId, fixtureId);
      
      // Start polling for batch results
      startBatchStepsPolling(sessionId, {
        setIsPolling,
        setTestStatus,
        setScreenshots,
        setError,
        pollIntervalRef
      });
    } catch (error) {
      console.error('Error running steps:', error);
      setError(`Failed to run steps: ${error.message}`);
      setIsPolling(false);
    }
  };

  const handleRunWithFixture = () => {
    closeFixtureModal();
    
    if (selectedFixture) {
      handleRunAllSteps(selectedFixture.id);
    } else {
      handleRunAllSteps();
    }
  };

  const handleResetScreenshotWrapper = async () => {
    await handleResetScreenshot(suiteId, {
      pollIntervalRef,
      setIsPolling,
      setTestStatus,
      setSessionId,
      setScreenshots,
      setInitialScreenshot,
      setError
    });
  };

  // Helper function to create a human-readable step name for display only
  const formatStepNameForDisplay = (step) => {
    switch (step.action) {
      case 'type':
        // Mask the value if the element is a password field or if secret is true
        const shouldMaskValue = step.element?.toLowerCase().includes('password') || step.secret === true;
        const displayValue = shouldMaskValue ? '••••••••' : step.value;
        
        // Add value mode indicator
        let valueModeIndicator = "";
        if (step.value_mode === "fixture") {
          valueModeIndicator = " (fixture)";
        } else if (step.value_mode === "random") {
          valueModeIndicator = " (random)";
        }
        
        return `Type "${displayValue}" into ${step.element}`;
      case 'click':
        return `Click on ${step.element}`;
      case 'navigate':
        return `Navigate to ${step.value}`;
      case 'select':
        return `Select "${step.value}" from "${step.element}"`;
      case 'enter':
        return `Press Enter key`;
      case 'workflow':
        const repeatInfo = step.repeatCount > 1 ? ` (${step.repeatCount}x)` : '';
        return `Run workflow: ${step.workflowName}${repeatInfo}`;
      case 'assert':
        return `Assert: ${step.value}`;
      case 'wait':
        // Convert milliseconds back to seconds for display
        const seconds = parseFloat(step.value) / 1000;
        return `Wait for ${seconds} second${seconds !== 1 ? 's' : ''}`;
      case 'reload':
        return `Reload page`;
      default:
        return `${step.action} ${step.element}`;
    }
  };

  // Original formatStepName function without masking for API use
  const formatStepName = (step) => {
    switch (step.action) {
      case "type":
        // Here we don't mask the value even for password fields since this is for API internal use
        let valueModeIndicator = "";
        if (step.value_mode === "fixture") {
          valueModeIndicator = " (fixture)";
        } else if (step.value_mode === "random") {
          valueModeIndicator = " (random)";
        }
        return `Type "${step.value}${valueModeIndicator}" into ${step.element}`;
      case "click":
        return `Click on ${step.element}`;
      case "navigate":
        return `Navigate to ${step.value}`;
      case "select":
        return `Select "${step.value}" from "${step.element}"`;
      case "enter":
        return `Press Enter key`;
      case "workflow":
        const repeatInfo = step.repeatCount > 1 ? ` (repeat ${step.repeatCount} times)` : '';
        return `Use Workflow: ${step.workflowName}${repeatInfo}`;
      case "assert":
        return `Assert: ${step.value}`;
      case "wait":
        // Convert milliseconds to seconds for display
        const seconds = parseFloat(step.value) / 1000;
        return `Wait for ${seconds} second${seconds !== 1 ? 's' : ''}`;
      case "reload":
        return `Reload page`;
      default:
        return `${step.action} ${step.element}`;
    }
  };

  // For single step polling
  const startSingleStepPolling = () => {
    if (isPolling) return;
    
    let pollIntervalId = null; // Store interval ID in closure
    setIsPolling(true);
    
    pollIntervalId = setInterval(async () => {
      try {
        const result = await apiEndpoints.getStepResult(sessionId);
        
        if (result.status === 'completed' || result.status === 'error' || result.status === 'failed') {
          if (pollIntervalId) {
            clearInterval(pollIntervalId);
            pollIntervalId = null;
          }
          setIsPolling(false);
          setTestStatus({
            status: result.status,
            description: result.status === 'error' 
              ? result.error || 'Step failed' 
              : result.step + ' completed successfully'
          });
        }
      } catch (error) {
        if (pollIntervalId) {
          clearInterval(pollIntervalId);
          pollIntervalId = null;
        }
        setIsPolling(false);
        setError('Failed to get step result: ' + error.message);
      }
    }, 3000);

    return () => {
      if (pollIntervalId) {
        clearInterval(pollIntervalId);
        pollIntervalId = null;
      }
    };
  };

  // Add pollInterval as a ref to track it across renders
  const pollIntervalRef = useRef(null);

  // Add this new function after handleRemoveStep
  const handleEditStep = (index) => {
    // Get the step we're editing
    const stepToEdit = { ...formData.savedSteps[index] };
    
    // Ensure repeatCount is present when editing a workflow step
    if (stepToEdit.action === 'workflow' && !stepToEdit.repeatCount) {
      stepToEdit.repeatCount = 1;
    }
    
    // For wait actions, convert milliseconds back to seconds for editing
    if (stepToEdit.action === 'wait') {
      stepToEdit.value = parseFloat(stepToEdit.value) / 1000; // Convert ms string to seconds
    }
    
    // Extract screenshot from metadata if present
    if (stepToEdit.metadata && stepToEdit.metadata.step_screenshot) {
      setStepScreenshot(stepToEdit.metadata.step_screenshot);
      setScreenshotPreview(stepToEdit.metadata.step_screenshot);
    } else {
      setStepScreenshot(null);
      setScreenshotPreview(null);
    }
    
    // Set the current step to the selected saved step
    setFormData(prev => ({
      ...prev,
      currentStep: stepToEdit,
      editingStepIndex: index // Track which step is being edited
    }));
  };

  // Add this function to handle drag end events
  const handleDragEnd = (result) => {
    // If dropped outside the list or no movement, do nothing
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // Create a copy of the saved steps
    const newSteps = Array.from(formData.savedSteps);
    
    // Remove the dragged item from its original position
    const [movedItem] = newSteps.splice(result.source.index, 1);
    
    // Insert the dragged item at its new position
    newSteps.splice(result.destination.index, 0, movedItem);
    
    // Update the order property for each step
    const reorderedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1
    }));
    
    // Update the state with the new order
    setFormData(prev => ({
      ...prev,
      savedSteps: reorderedSteps
    }));
  };

  // Handler for the password checkbox
  const handleToggleSecret = () => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        secret: !prev.currentStep.secret
      }
    }));
  };

  // Add value_mode handler
  const handleValueModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        value_mode: mode
      }
    }));
  };

  const openFixtureModal = () => {
    setIsFixtureModalOpen(true);
  };

  const closeFixtureModal = () => {
    setIsFixtureModalOpen(false);
  };

  const handleFixtureSelect = (fixture) => {
    setSelectedFixture(fixture);
    handleRunAllSteps(fixture?.id);
  };

  const handleRunWithoutFixture = () => {
    setSelectedFixture(null);
    handleRunAllSteps();
  };

  // Add this function to format API call name for display
  const formatApiCallNameForDisplay = (call) => {
    return `${call.method} ${call.name}`;
  };

  // Combine steps and API calls for display
  const getCombinedStepsForDisplay = () => {
    // Create API call step objects
    const apiCallSteps = savedApiCalls.map(call => ({
      isApiCall: true,
      ...call
    }));
    
    // Create regular step objects
    const regularSteps = formData.savedSteps.map(step => ({
      isApiCall: false,
      ...step
    }));
    
    // Combine all steps
    return [...regularSteps, ...apiCallSteps].sort((a, b) => a.order - b.order);
  };

  // Add this function to handle editing a step from SavedSteps
  const handleEditStepFromList = (step) => {
    const stepIndex = formData.savedSteps.findIndex(s => s.order === step.order);
    handleEditStep(stepIndex);
  };

  // Add this function to handle removing a step from SavedSteps
  const handleRemoveStepFromList = (step) => {
    const stepIndex = formData.savedSteps.findIndex(s => s.order === step.order);
    handleRemoveStep(stepIndex);
  };

  // Add this function to handle editing an API call from SavedSteps
  const handleEditApiCallFromList = (apiCall) => {
    const apiCallIndex = savedApiCalls.findIndex(call => call.id === apiCall.id);
    handleEditApiCall(apiCallIndex);
  };

  // Add this function to handle removing an API call from SavedSteps
  const handleRemoveApiCallFromList = (apiCall) => {
    const apiCallIndex = savedApiCalls.findIndex(call => call.id === apiCall.id);
    handleRemoveApiCall(apiCallIndex);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div>
          <div className="flex items-center gap-4 mb-6">
            <button 
              onClick={() => {
                const hasUnsavedChanges = formData.testname.trim() !== '' || formData.savedSteps.length > 0 || savedApiCalls.length > 0;
                
                if (hasUnsavedChanges) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to leave this page?')) {
                    navigate(`/testsuites/${suiteId}/tests`);
                  }
                } else {
                  navigate(`/testsuites/${suiteId}/tests`);
                }
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Test Cases
            </button>
            <button
              onClick={handleSubmit}
              className="bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors"
            >
              Save Test Case
            </button>
          </div>

          <div className="mb-8 grid grid-cols-5 gap-8">
            <div className="col-span-2">
              <h1 className="text-2xl font-bold text-gray-900 mb-6">Create Test Case</h1>
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="testname" className="block text-sm font-medium text-gray-700 mb-2">
                      Test Name
                    </label>
                    <input
                      type="text"
                      id="testname"
                      name="testname"
                      value={formData.testname}
                      onChange={(e) => setFormData(prev => ({ ...prev, testname: e.target.value }))}
                      className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                    />
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700">Test Steps</label>
                    <button
                      onClick={handleGenerateSteps}
                      disabled={isGeneratingSteps || !formData.testname}
                      className={`${
                        isGeneratingSteps || !formData.testname
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-black hover:bg-gray-800"
                      } text-white font-medium py-2 px-4 rounded transition-colors`}
                    >
                      {isGeneratingSteps ? "Generating..." : "Generate Steps"}
                    </button>
                  </div>

                  {/* Replace the old saved steps list with the new SavedSteps component */}
                  {(formData.savedSteps.length > 0 || savedApiCalls.length > 0) && (
                    <SavedSteps
                      steps={getCombinedStepsForDisplay()}
                      onDragEnd={handleDragEnd}
                      onEditStep={handleEditStepFromList}
                      onRemoveStep={handleRemoveStepFromList}
                      onEditApiCall={handleEditApiCallFromList}
                      onRemoveApiCall={handleRemoveApiCallFromList}
                      formatStepNameForDisplay={formatStepNameForDisplay}
                      formatApiCallNameForDisplay={formatApiCallNameForDisplay}
                    />
                  )}

                  <GeneratedSteps 
                    steps={pendingSteps}
                    showGeneratedSteps={showGeneratedSteps}
                    onAccept={handleAcceptSteps}
                    onReject={handleRejectSteps}
                  />

                  {/* Add new step section */}
                  <TestStepForm
                    apiCallMode={apiCallMode}
                    setApiCallMode={setApiCallMode}
                    formData={formData}
                    setFormData={setFormData}
                    handleSaveStep={handleSaveStep}
                    handleToggleSecret={handleToggleSecret}
                    handleValueModeChange={handleValueModeChange}
                    handleGenerateAssertion={handleGenerateAssertion}
                    isAssertionGenerating={isAssertionGenerating}
                    genratedAssertions={genratedAssertions}
                    TEST_ACTIONS={TEST_ACTIONS}
                    availableWorkflows={availableWorkflows}
                    isLoadingApiCalls={isLoadingApiCalls}
                    apiCallsError={apiCallsError}
                    availableApiCalls={availableApiCalls}
                    selectedApiCallId={selectedApiCallId}
                    setSelectedApiCallId={setSelectedApiCallId}
                    handleSaveApiCall={handleSaveApiCall}
                    editingApiCallIndex={editingApiCallIndex}
                    setApiCall={setApiCall}
                    setEditingApiCallIndex={setEditingApiCallIndex}
                    handleKeyPress={handleKeyPress}
                    error={error}
                    stepScreenshot={stepScreenshot}
                    setStepScreenshot={setStepScreenshot}
                    screenshotPreview={screenshotPreview}
                    setScreenshotPreview={setScreenshotPreview}
                    isUploadingScreenshot={isUploadingScreenshot}
                    setIsUploadingScreenshot={setIsUploadingScreenshot}
                  />


                </div>
              </div>
            </div>

            <div className="col-span-3">
              <ScreenshotPreview
                title="Screenshot Preview"
                isPolling={isPolling}
                screenshots={[initialScreenshot,...screenshots]}
                testStatus={testStatus}
                pollingMessage="Running test case..."
                loadingMessage="Running test steps..."
                successMessage="All test steps completed successfully."
                onReset={handleResetScreenshotWrapper}
                onRun={isPolling ? handleRunAllSteps : openFixtureModal}
                runButtonDisabled={formData.savedSteps.length === 0}
                runButtonText={isPolling ? "Cancel" : "Run from start"}
                runButtonClass={isPolling ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}    
              />
            </div>
          </div>
        </div>
      </div>

      <DataFixture 
        isOpen={isFixtureModalOpen}
        onClose={closeFixtureModal}
        onSelect={handleFixtureSelect}
        onRunWithoutFixture={handleRunWithoutFixture}
      />
    </div>
  );
}

export default CreateTestCase; 